import { z } from "zod";
import { DvhChainSchema, SalesTypeSchema } from "./business-organisation";

// ---------------------------------------------------------------------------
// Onboarding draft (GRA-171)
//
// The six-step onboarding saves a draft after every step so a reload (or a
// return days later) resumes where the admin left off. The draft lives on
// `Businesses/{id}.onboardingDraft` and therefore starts at the organisation
// step, the first one with a business doc; the account and sector steps are
// held in the browser until then. It is deleted once the test invitations
// have been sent.
// ---------------------------------------------------------------------------

export const ONBOARDING_V2_STEPS = [
  "account",
  "sector",
  "organisation",
  "employees",
  "teams",
  "invite",
] as const;
export type OnboardingV2Step = (typeof ONBOARDING_V2_STEPS)[number];
export const OnboardingV2StepSchema = z.enum(ONBOARDING_V2_STEPS);

/** Where a server draft can point: the organisation step onward, or finished. */
export const ONBOARDING_DRAFT_STEPS = [
  "organisation",
  "employees",
  "teams",
  "invite",
  "done",
] as const;
export type OnboardingDraftStep = (typeof ONBOARDING_DRAFT_STEPS)[number];
export const OnboardingDraftStepSchema = z.enum(ONBOARDING_DRAFT_STEPS);

export const ONBOARDING_MAX_EMPLOYEE_ROWS = 200;
/** Firestore documents cap at 1 MiB; the draft shares the business doc. */
export const ONBOARDING_DRAFT_MAX_BYTES = 400_000;

/** One row of the employees table as typed or pasted; may still be invalid. */
export const EmployeeDraftRowSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().max(160),
  email: z.string().max(254),
  mobile: z.string().max(32).optional(),
  team: z.string().max(120),
  role: z.string().max(160).optional(),
  employeeNumber: z.string().max(32).optional(),
});
export type EmployeeDraftRow = z.infer<typeof EmployeeDraftRowSchema>;

export const OnboardingDraftOrganisationSchema = z
  .object({
    unitName: z.string().max(120),
    unitNumber: z.string().max(40),
    headcount: z.number().int().min(0).max(100_000),
    chain: DvhChainSchema,
    salesType: SalesTypeSchema,
  })
  .partial();

export const OnboardingDraftEmployeesSchema = z.object({
  fileName: z.string().max(200).nullable().default(null),
  rows: z.array(EmployeeDraftRowSchema).max(ONBOARDING_MAX_EMPLOYEE_ROWS),
  /** ISO instant of the last successful import; rows are emptied then. */
  importedAt: z.string().nullable().default(null),
  importSummary: z
    .object({
      created: z.number().int().min(0),
      updated: z.number().int().min(0),
      invalid: z.number().int().min(0),
      teamsCreated: z.number().int().min(0),
    })
    .nullable()
    .default(null),
});

export const OnboardingDraftTeamsSchema = z.object({
  /** Raw team value from the employees table -> display name chosen by the admin. */
  names: z.record(z.string().max(120), z.string().max(120)),
});

export const OnboardingDraftInvitationsSchema = z
  .object({
    greeting: z.string().max(2000).nullable(),
    greetingSource: z.enum(["ai", "manual", "fallback"]).nullable(),
    sendMode: z.enum(["now", "scheduled"]),
    scheduledFor: z.string().nullable(),
    remindersEnabled: z.boolean(),
    submittedBatchId: z.string().nullable(),
  })
  .partial();

export const OnboardingDraftSchema = z.object({
  version: z.literal(1),
  currentStep: OnboardingDraftStepSchema,
  completedSteps: z.array(OnboardingV2StepSchema).default([]),
  account: z.object({ mobile: z.string().max(32).optional() }).optional(),
  organisation: OnboardingDraftOrganisationSchema.optional(),
  employees: OnboardingDraftEmployeesSchema.optional(),
  teams: OnboardingDraftTeamsSchema.optional(),
  invitations: OnboardingDraftInvitationsSchema.optional(),
});
export type OnboardingDraft = z.infer<typeof OnboardingDraftSchema>;

/** A partial save: only the sections present are written. */
export const OnboardingDraftPatchSchema = z.object({
  currentStep: OnboardingDraftStepSchema.optional(),
  /** Appended (array union); never removes earlier entries. */
  completedSteps: z.array(OnboardingV2StepSchema).optional(),
  account: z.object({ mobile: z.string().max(32).optional() }).optional(),
  organisation: OnboardingDraftOrganisationSchema.optional(),
  employees: OnboardingDraftEmployeesSchema.optional(),
  teams: OnboardingDraftTeamsSchema.optional(),
  invitations: OnboardingDraftInvitationsSchema.optional(),
});
export type OnboardingDraftPatch = z.infer<typeof OnboardingDraftPatchSchema>;

export const SaveOnboardingDraftInput = z.object({
  businessId: z.string().min(1),
  patch: OnboardingDraftPatchSchema,
});
export type SaveOnboardingDraftInput = z.infer<typeof SaveOnboardingDraftInput>;

/** Reads a stored draft leniently: an unparsable draft reads as none. */
export function parseOnboardingDraft(raw: unknown): OnboardingDraft | null {
  const result = OnboardingDraftSchema.safeParse(raw);
  return result.success ? result.data : null;
}
